"use client"

import { ProxyImage } from "@/components/ui/ProxyImage"
import { Card, CardContent } from "@/components/ui/card"
import { Progress } from "@/components/ui/progress"
import { getImagePath } from "@/lib/helpers/imageHelpers"
import { formatUnixTime } from "@/lib/utils"
import Link from "@/components/Link"
import Rating from "../Rating"
import { Price } from "../price"
import { CourseActions } from "./CourseActions"
import { CourseMetadata } from "./CourseMetadata"
import type { CourseListItemProps } from "./types"

export const CourseListItem = ({
	data,
	type = "course",
	refetch,
	cartData,
	showPercentLearned = false,
	showWhiteList = true,
}: CourseListItemProps) => {
	const totalStudents =
		data.number_of_students + data.count_user + data.number_of_registrations

	const percentageLearned = showPercentLearned && (
		<div className="flex items-center gap-2">
			<span className="font-medium text-xs sm:text-sm">Đã học:</span>
			<Progress
				value={Math.round(data.percentage_learned)}
				className="w-20 sm:w-24"
			/>
			<span className="text-muted-foreground text-xs sm:text-sm">
				{Math.round(data.percentage_learned)}%
			</span>
		</div>
	)

	const priceContent = Price.onList(data)

	return (
		<Card className="overflow-hidden transition-shadow duration-300 hover:shadow-lg">
			<Link href={`/course/${data.slug}`}>
				<CardContent className="p-2 sm:p-2">
					<div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
						<div className="relative mx-auto w-full sm:mx-0 sm:h-32 sm:w-48">
							<ProxyImage
								src={getImagePath(data, "course", "lg", true)}
								alt={data.name}
								width={192}
								height={108}
								className="rounded-lg object-cover"
							/>
							<CourseActions
								id={data.id}
								isWhitelist={data.is_whitelist}
								showWhiteList={showWhiteList}
								cartData={cartData}
								type={type}
								refetch={refetch}
							/>
						</div>
						<div className="flex-1 space-y-2 text-left sm:space-y-3">
							<div>
								<h3 className="line-clamp-2 font-semibold text-base leading-tight transition-colors hover:text-primary sm:text-lg">
									{data.name}
								</h3>
								<p className="mt-1.5 text-muted-foreground text-xs sm:text-sm">
									{data.lecturer_name} • {formatUnixTime(data.start_date)}
								</p>
							</div>
							<p className="line-clamp-2 text-muted-foreground text-xs leading-normal sm:text-sm">
								{data.course_objective}
							</p>
							<div className="sm:flex sm:items-center sm:justify-between">
								<div className="flex flex-row flex-wrap items-center gap-2 sm:flex-row sm:items-center">
									<div className="flex items-center gap-1.5">
										<span className="text-yellow-400">
											<Rating
												disabled={true}
												defaultValue={totalStudents ? 5 : 0}
												allowHalf={true}
											/>
										</span>
										<span className="text-muted-foreground text-xs sm:text-sm">
											({totalStudents})
										</span>
									</div>
									<div className="flex items-center gap-2 sm:hidden">
										{priceContent}
										{percentageLearned}
										<CourseActions
											id={data.id}
											isWhitelist={data.is_whitelist}
											showWhiteList={showWhiteList}
											cartData={cartData}
											type={type}
											refetch={refetch}
										/>
									</div>
								</div>
								<div className="hidden sm:flex sm:items-center sm:gap-3">
									{priceContent}
									{percentageLearned}
								</div>
							</div>
							<div className="hidden sm:block">
								<CourseMetadata
									totalVideos={data.total_videos}
									hours={data.hour}
									totalStudents={totalStudents}
								/>
							</div>
							<div className="sm:hidden">
								<CourseMetadata
									totalVideos={data.total_videos}
									hours={data.hour}
									totalStudents={totalStudents}
								/>
							</div>
						</div>
					</div>
				</CardContent>
			</Link>
		</Card>
	)
}
